import React, { useState, useEffect } from 'react';
import { allPayers as payersDatabase } from '../../../data/payersData';
import { getLicensedStates, isPayerRelevantToStates } from '../../../utils/payerStateUtils';

interface Payer {
  id: string;
  payerId: string;
  name: string;
  address: string;
}

interface SearchPayersModalProps {
  onClose: () => void;
  onAddPayer: (payer: Payer) => void;
  existingPayerIds: string[];
}

const SearchPayersModal: React.FC<SearchPayersModalProps> = ({ onClose, onAddPayer, existingPayerIds }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPayers, setFilteredPayers] = useState<Payer[]>([]);
  const [filterByState, setFilterByState] = useState(false);
  const licensedStates = getLicensedStates();

  // Import payers from external data file and sort alphabetically by name
  const allPayers = [...payersDatabase].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  useEffect(() => {
    let filtered = allPayers;

    // Filter by licensed states if toggle is on
    if (filterByState && licensedStates.length > 0) {
      filtered = filtered.filter(p => isPayerRelevantToStates(p, licensedStates));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (payer) =>
          payer.name.toLowerCase().includes(term) ||
          payer.payerId.toLowerCase().includes(term) ||
          payer.address.toLowerCase().includes(term)
      );
    }

    setFilteredPayers(filtered);
  }, [searchTerm, filterByState]);

  const isPayerAdded = (payerId: string) => {
    return existingPayerIds.includes(payerId);
  };

  const handleAddPayer = (payer: Payer) => {
    onAddPayer(payer);
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-payers-title"
        aria-describedby="search-payers-description"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 id="search-payers-title" style={{ fontSize: '20px', fontWeight: 600, color: '#111827', margin: '0 0 6px 0' }}>
                Search available payers
              </h2>
              <p id="search-payers-description" style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                You can edit the payers' details after you add them to your list{' '}
                <span style={{ color: '#1E90FF' }}>
                  Learn more
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close search payers dialog"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1'
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ position: 'relative' }}>
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9CA3AF',
                fontSize: '16px'
              }}
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="Search"
              aria-label="Search payers by name, ID, or address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                backgroundColor: '#F9FAFB',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#374151'
              }}
            />
          </div>
        </div>

        {/* State Filter */}
        {licensedStates.length > 0 && (
          <div style={{ padding: '8px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
              <input
                type="checkbox"
                checked={filterByState}
                onChange={(e) => setFilterByState(e.target.checked)}
              />
              Show only payers for my licensed states ({licensedStates.join(', ')})
            </label>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
              {filterByState ? `${filteredPayers.length} payers` : `${allPayers.length} total payers`}
            </span>
          </div>
        )}

        {/* Table Header */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 120px', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>Payer ID</span>
              <span style={{ color: '#9CA3AF', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
            </div>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>Name</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>Add payer</span>
          </div>
        </div>

        {/* Scrollable List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {filteredPayers.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
              No payers found. Try a different search term.
            </div>
          ) : (
            filteredPayers.map((payer) => (
              <div
                key={payer.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr 120px',
                  gap: '16px',
                  padding: '12px 0',
                  borderBottom: '1px solid #E5E7EB',
                  alignItems: 'center'
                }}
              >
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{payer.payerId}</div>
                <div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: 500, marginBottom: '3px' }}>
                    {payer.name}
                  </div>
                  {payer.address && (
                    <div style={{ fontSize: '13px', color: '#6B7280' }}>{payer.address}</div>
                  )}
                </div>
                <div>
                  {isPayerAdded(payer.payerId) ? (
                    <button
                      disabled
                      style={{
                        padding: '6px 16px',
                        backgroundColor: '#10B981',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      ✓ Added
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAddPayer(payer)}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#E5E7EB';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                    >
                      + Add
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPayersModal;
